import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { getDocument } from '@atlaskit/browser-apis';

import type { PublicPageFeatureFlags } from '@atlassian/status-page';

// Type for server data
interface ServerData {
	pageData: any;
	componentData: any;
	pathname: string;
	pageUptime: any;
	pageComponentsUptime: any;
	incidents: any;
	currentIncident: any;
	fromUrl?: string;
	featureFlags?: PublicPageFeatureFlags;
	loadingFileIcon?: string;
	logoIcon?: string;
}

// Get server data from global variable
const getServerData = (): ServerData | null => {
	return (window as any).__SERVER_DATA__ || null;
};

// Helper to resolve the correct page component based on the current path
const getPageComponent = async () => {
	const serverData = getServerData();

	if (serverData?.pageData) {
		const pageStatus = serverData.pageData?.pageStatus;
		const isInactivePage =
			serverData.featureFlags?.stakeholderCommsSoftDeletionFlow &&
			(pageStatus === 'ARCHIVED' ||
				pageStatus === 'SOFT_DELETED' ||
				pageStatus === 'UNPUBLISHED' ||
				pageStatus === 'UNPUBLISHED_WITH_CHANGES');

		if (isInactivePage) {
			const module = await import(/* webpackChunkName: "inactive-page" */ '../pages/InactivePage');
			return module.default;
		}
	}

	const path = window.location.pathname;
	if (path === '/history') {
		// Dynamically import HistoryPage for code splitting
		const module = await import(
			/* webpackChunkName: "@atlaskit-internal_status-page-history" */ '../pages/HistoryPage'
		);
		return module.default;
	}
	// Check if it's an incident detail page (/incidents/:incidentId)
	if (/^\/incidents\/[^/]+$/.test(path)) {
		const module = await import(
			/* webpackChunkName: "@atlaskit-internal_status-page-incident-detail" */ '../pages/IncidentDetailPage'
		);
		return module.default;
	}
	// Default to LiveStatusPage for root and other paths
	const module = await import(
		/* webpackChunkName: "@atlaskit-internal_status-page-live-status" */ '../pages/LiveStatusPage'
	);
	return module.default;
};

// Helper to run callback after React hydration completes
// Uses requestIdleCallback with setTimeout fallback to wait for browser idle state
const runAfterHydration = (callback: () => void) => {
	if (typeof requestIdleCallback !== 'undefined') {
		requestIdleCallback(callback, { timeout: 0 });
	} else {
		// Fallback for browsers that don't support requestIdleCallback
		setTimeout(callback, 0);
	}
};

// Hydrate the SSR markup
getPageComponent().then((PageComponent) => {
	const doc = getDocument();
	if (doc) {
		const container = doc.getElementById('root');
		if (container) {
			const serverData = getServerData();

			// Use the same props that were used during SSR
			if (serverData) {
				const pageStatus = serverData.pageData?.pageStatus;
				const isArchivedPage =
					serverData.featureFlags?.stakeholderCommsSoftDeletionFlow &&
					(pageStatus === 'ARCHIVED' ||
						pageStatus === 'SOFT_DELETED' ||
						pageStatus === 'UNPUBLISHED' ||
						pageStatus === 'UNPUBLISHED_WITH_CHANGES');

				if (isArchivedPage) {
					hydrateRoot(
						container,
						<PageComponent
							pageData={serverData.pageData}
							loadingFileIcon={serverData.loadingFileIcon}
							logoIcon={serverData.logoIcon}
						/>,
					);
				} else {
					hydrateRoot(
						container,
						<PageComponent
							pageData={serverData.pageData}
							componentData={serverData.componentData}
							pageUptime={serverData.pageUptime}
							pageComponentsUptime={serverData.pageComponentsUptime}
							incidents={serverData.incidents}
							currentIncident={serverData.currentIncident}
							fromUrl={serverData.fromUrl}
							featureFlags={serverData.featureFlags}
						/>,
					);
				}

				// Run custom scripts AFTER hydration completes to avoid hydration mismatch
				runAfterHydration(() => {
					if (typeof document !== 'undefined' && document) {
						const customCss = document.getElementById('custom-css');
						if (customCss) {
							const newStyleTag = document.createElement('style');
							newStyleTag.setAttribute('id', 'custom-css-updated');
							newStyleTag.innerHTML = serverData?.pageData?.customCss || '';
							customCss.remove();
							document.head.appendChild(newStyleTag);
						}

						// Helper to insert HTML and execute any scripts within it
						const insertHtmlWithScripts = (containerEl: HTMLElement, html: string) => {
							containerEl.innerHTML = html;
							// Scripts added via innerHTML don't execute, so we need to manually execute them
							const scripts = containerEl.querySelectorAll('script');
							scripts.forEach((oldScript) => {
								const newScript = document.createElement('script');
								// Copy all attributes
								Array.from(oldScript.attributes).forEach((attr) => {
									newScript.setAttribute(attr.name, attr.value);
								});
								// Wrap script content in IIFE to create local scope and prevent variable re-declaration errors
								let scriptContent = oldScript.innerHTML.trim();
								// Decode escape sequences that may have been preserved from JSON serialization
								scriptContent = scriptContent
									.replace(/\\n/g, '\n')
									.replace(/\\t/g, '\t')
									.replace(/\\r/g, '\r');
								if (scriptContent && !oldScript.src) {
									newScript.innerHTML = `(function() { ${scriptContent} })();`;
								} else {
									newScript.innerHTML = scriptContent;
								}
								oldScript.parentNode?.replaceChild(newScript, oldScript);
							});
						};

						// Insert custom header HTML if provided
						const customHeaderHtml = serverData?.pageData?.customHeaderHtml;
						const headerContainer = document.getElementById('custom-header-container');
						if (customHeaderHtml && headerContainer) {
							insertHtmlWithScripts(headerContainer, customHeaderHtml);
						}

						// Insert custom footer HTML if provided
						const customFooterHtml = serverData?.pageData?.customFooterHtml;
						const footerContainer = document.getElementById('custom-footer-container');
						if (customFooterHtml && footerContainer) {
							insertHtmlWithScripts(footerContainer, customFooterHtml);
						}
					}
				});
			} else {
				// Fallback for when no server data is available
				hydrateRoot(container, <PageComponent />);
			}
		}
	}
});
